import { Component } from '@angular/core';
import {
  ActivityInternalListComponent,
  AdminGuard,
  AuthGuard,
  CustomPageDisplayComponent,
  CustomPageResolverService,
  DocumentsComponent,
  featureActivatedGuard,
  SalesGuard
} from 'gung-standard';
import { ColmecProductDetailsComponent } from './components/colmec-product-details/colmec-product-details.component';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { registerLocaleData } from '@angular/common';

import localeSv from '@angular/common/locales/sv';
import localeEn from '@angular/common/locales/en';
import { ColmecHomeComponent } from './components/colmec-home/colmec-home.component';
import { ColmecOrderConfirmationComponent } from './components/checkout/colmec-order-confirmation/colmec-order-confirmation.component';
import { ColmecOrderDetailsComponent } from './components/colmec-order-details/colmec-order-details.component';
import { ColmecCustomerDetailsComponent } from './components/colmec-customer-details/colmec-customer-details.component';
import { ColmecProductListComponent } from './components/product/colmec-product-list/colmec-product-list.component';
import { ColmecLoginResetPasswordWrapperComponent } from './components/colmec-login-reset-password-wrapper/colmec-login-reset-password-wrapper.component';
import { ColmecHomeUserComponent } from './components/colmec-home-user/colmec-home-user.component';
import { ColmecCheckoutWrapperComponent } from './components/checkout/colmec-checkout-wrapper/colmec-checkout-wrapper.component';
import { ColmecCustomPageResolverService } from './services/colmec-custom-page-resolver.service';
import { ColmecConceptDetailsComponent } from './components/colmec-concept-details/colmec-concept-details.component';
import { ColmecClaimListComponent } from './components/colmec-claim/colmec-claim-list/colmec-claim-list.component';
import { environment } from '../environments/environment';
import { ColmecPartnerAgreementsListComponent } from './components/colmec-partner-agreements-list/colmec-partner-agreements-list.component';
import { ColmecWorkingOrderWrapperComponent } from './components/working-order/colmec-working-order-wrapper/colmec-working-order-wrapper.component';
import { ColmecDiscountsComponent } from './components/colmec-discounts/colmec-discounts.component';
import { ColmecTirePolicyListComponent } from './components/tire-policy/colmec-tire-policy-list/colmec-tire-policy-list.component';
import { ColmecWorkingOrderListComponent } from './components/working-order/colmec-working-order-list/colmec-working-order-list.component';
import { ColmecWorkingOrderDetailsComponent } from './components/working-order/colmec-working-order-details/colmec-working-order-details.component';
import { ColmecPimProductComponent } from './components/colmec-pim-product/colmec-pim-product.component';
@Component({
    selector: 'colmec-root',
    template: `<colmec-app-wrapper></colmec-app-wrapper>`,
    styles: [],
    standalone: false
})
export class ColmecAppComponent {
  title = 'colmec';

  constructor(private router: Router, translate: TranslateService) {
    const lang = localStorage.getItem('selectedLanguage');
    translate.use(lang || 'se');
    registerLocaleData(localeSv);
    registerLocaleData(localeEn);
    this.setupRoutes();

    translate.onLangChange.subscribe((event: LangChangeEvent) => {
      localStorage.setItem('selectedLanguage', event.lang);
    });
  }

  private setupRoutes() {
    const routerConfig = this.router.config;
    this.router.resetConfig([
      {
        path: '',
        component: ColmecHomeComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'login',
        component: ColmecLoginResetPasswordWrapperComponent
      },
      {
        path: 'forgot-password',
        component: ColmecLoginResetPasswordWrapperComponent
      },
      {
        path: 'categories/:assortmentId',
        canActivate: [AuthGuard],
        component: ColmecProductListComponent
      },
      {
        path: 'product/:productId',
        component: ColmecProductDetailsComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'concept/:productId',
        component: ColmecConceptDetailsComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'order-confirmation/:contextId',
        canActivate: [AuthGuard],
        component: ColmecOrderConfirmationComponent
      },
      {
        path: 'orders/:orderId',
        component: ColmecOrderDetailsComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'customers/:customerId',
        component: ColmecCustomerDetailsComponent,
        canActivate: [AuthGuard]
      },
      {
        path: 'admin/upload-documents',
        component: DocumentsComponent,
        canActivate: [AuthGuard]
      },
      /* {
        path: 'home',
        component: ColmecHomeUserComponent,
        canActivate: [AuthGuard]
      }, */
      {
        path: 'checkout',
        canActivate: [AuthGuard],
        component: ColmecCheckoutWrapperComponent
      },
      {
        path: 'home',
        component: CustomPageDisplayComponent,
        resolve: {
          pageComponent: ColmecCustomPageResolverService
        },
        data: {
          pageName: 'home'
          // pageComponent: ColmecHomeUserComponent
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'orders/:orderId',
        canActivate: [AuthGuard],
        component: ColmecOrderDetailsComponent
      },
      {
        path: 'claims',
        redirectTo: environment.enableClaim ? undefined : '/',
        canActivate: environment.enableClaim ? [AuthGuard] : undefined,
        component: environment.enableClaim ? ColmecClaimListComponent : undefined
        // children: [
        //   {
        //     path: 'create',
        //     canActivate: [AuthGuard],
        //     component: ColmecOrderDetailsComponent,
        //   },
        //   {
        //     path: ':id',
        //     canActivate: [AuthGuard],
        //     component: ColmecOrderDetailsComponent,
        //   },
        // ]
      },
      {
        path: 'partner-agreements',
        canActivate: [AuthGuard, SalesGuard],
        component: ColmecPartnerAgreementsListComponent
      },
      {
        path: 'working-order',
        canActivate: [AuthGuard],
        canDeactivate: [
          (component: ColmecWorkingOrderWrapperComponent) => component.canDeactivate ? component.canDeactivate() : true
        ],
        component: ColmecWorkingOrderWrapperComponent
      },
      {
        path: 'working-order/:id',
        canActivate: [AuthGuard],
        canDeactivate: [
          (component: ColmecWorkingOrderWrapperComponent) => component.canDeactivate ? component.canDeactivate() : true
        ],
        component: ColmecWorkingOrderWrapperComponent
      },
      {
        path: 'discounts',
        canActivate: [AuthGuard, AdminGuard],
        component: ColmecDiscountsComponent
      },
      {
        path: 'tire-policy',
        canActivate: [AuthGuard, AdminGuard],
        component: ColmecTirePolicyListComponent
      },
      {
        path: 'work-orders',
        canActivate: [AuthGuard],
        component: ColmecWorkingOrderListComponent
      },
      {
        path: 'work-order-details/:id',
        canActivate: [AuthGuard],
        component: ColmecWorkingOrderDetailsComponent
      },
      {
        path: 'pim/:productId',
        component: ColmecPimProductComponent,
        canActivate: [AuthGuard, AdminGuard, featureActivatedGuard],
        canDeactivate: [
          (
            component: ColmecPimProductComponent,
            currentRoute: ActivatedRouteSnapshot,
            currentState: RouterStateSnapshot,
            nextState: RouterStateSnapshot
          ) => !component.hasUnsavedChanges(currentRoute, currentState, nextState)
        ],
        data: {
          featureId: 'gung-internal-pim',
          minimumGungBaseVersion: '3.0.11'
        }
      },
      ...routerConfig
    ]);
  }
}
